// src/pages/dashboard/HelpCenterTopic.js
// Help Center topic page — accordion article list, role-aware
// Route: /dashboard/settings/support/:categorySlug or /help/:categorySlug

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { getCategoryBySlug } from '../../data/helpCenterContent';
import SupportContactForm from '../../components/support/SupportContactForm';

const HelpCenterTopic = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [openArticleId, setOpenArticleId] = useState(null);

  const category = getCategoryBySlug(categorySlug);

  const userRole = localStorage.getItem('userRole') || 'practitioner';
  const isPractitioner = userRole === 'practitioner' || userRole === 'admin';
  const isHelpRoute = location.pathname.startsWith('/help');
  const backPath = isHelpRoute ? '/help' : '/dashboard/settings/support';

  // Auto-open article from search result link (?article=id)
  useEffect(() => {
    const articleId = searchParams.get('article');
    if (articleId && category?.articles.some(a => a.id === articleId)) {
      setOpenArticleId(articleId);
      // Scroll to article after render
      setTimeout(() => {
        const el = document.getElementById(`article-${articleId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [searchParams, category]);

  const toggleArticle = (id) => {
    setOpenArticleId((prev) => (prev === id ? null : id));
  };

  // 404 state
  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-3">Topic not found</h1>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate(backPath)}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(backPath); }}
            className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1 mx-auto cursor-pointer"
            style={{ userSelect: 'none' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Center
          </span>
        </div>
      </div>
    );
  }

  // Empty category (seeker placeholders)
  if (category.articles.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate(backPath)}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(backPath); }}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors mb-7 cursor-pointer"
            style={{ userSelect: 'none' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Center
          </span>

          <div className="text-center py-16">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 ${category.iconBg}`}>
              <category.Icon className={`w-8 h-8 ${category.iconColor}`} />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">{category.title}</h1>
            <p className="text-sm text-gray-500">Articles for this category are coming soon.</p>
          </div>

          <SupportContactForm />
        </div>
      </div>
    );
  }

  const { Icon } = category;

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">

        {/* Breadcrumb */}
        {isPractitioner && !isHelpRoute ? (
          <nav className="flex items-center gap-2 text-sm mb-6 flex-wrap" aria-label="Breadcrumb">
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate('/dashboard/practitioner')}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/practitioner'); }}
              className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            >
              Dashboard
            </span>
            <span className="text-gray-400">/</span>
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate('/dashboard/settings')}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/settings'); }}
              className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            >
              Settings
            </span>
            <span className="text-gray-400">/</span>
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate(backPath)}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(backPath); }}
              className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            >
              Help Center
            </span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium" aria-current="page">{category.title}</span>
          </nav>
        ) : null}

        {/* Back link */}
        <span
          role="button"
          tabIndex={0}
          onClick={() => navigate(backPath)}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(backPath); }}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors mb-7 cursor-pointer"
          style={{ userSelect: 'none' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Help Center
        </span>

        {/* Category header */}
        <div className="flex items-center gap-4 mb-8">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${category.iconBg}`}>
            <Icon className={`w-6 h-6 ${category.iconColor}`} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{category.title}</h1>
            <p className="text-sm text-gray-600 mt-0.5">{category.description}</p>
          </div>
        </div>

        {/* Accordion articles */}
        <div className="space-y-3 mb-8">
          {category.articles.map((article) => {
            const isOpen = openArticleId === article.id;
            return (
              <div
                key={article.id}
                id={`article-${article.id}`}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleArticle(article.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') toggleArticle(article.id); }}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  style={{ userSelect: 'none' }}
                  aria-expanded={isOpen}
                >
                  <span className="font-medium text-gray-900 pr-4">{article.title}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </span>

                {isOpen && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <div className="pt-5 space-y-3">
                      {article.paragraphs.map((para, i) => (
                        <p key={i} className="text-sm text-gray-600 leading-relaxed">
                          {para}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Support Contact Form */}
        <SupportContactForm />
      </div>
    </div>
  );
};

export default HelpCenterTopic;
